import { api } from "@/lib/orgApi";

export const ACTCODE = "http://terminology.hl7.org/CodeSystem/v3-ActCode";
export const SNOMED = "http://snomed.info/sct";

// FHIR R4 Encounter.class — UI `display` is separate from ActCode `code`.
// System: http://terminology.hl7.org/CodeSystem/v3-ActCode
// Value set: http://terminology.hl7.org/ValueSet/encounter-class
export const ENCOUNTER_CLASSES = [
  { code: "AMB", display: "Ambulatory" },
  { code: "EMER", display: "Emergency" },
  { code: "IMP", display: "Inpatient" },
  { code: "OBSENC", display: "Observation" },
  { code: "VR", display: "Virtual" },
  { code: "HH", display: "Home Health" },
];

export const ENCOUNTER_STATUSES = [
  { code: "planned", display: "Planned" },
  { code: "arrived", display: "Arrived" },
  { code: "triaged", display: "Triaged" },
  { code: "in-progress", display: "In Progress" },
  { code: "onleave", display: "On Leave" },
  { code: "finished", display: "Completed" },
  { code: "cancelled", display: "Cancelled" },
];

export const ENCOUNTER_TYPES = [
  { code: "185349003", display: "Encounter for check-up" },
  { code: "390906007", display: "Follow-up encounter" },
  { code: "270427003", display: "Patient-initiated encounter" },
  { code: "448337001", display: "Telemedicine consultation" },
  { code: "50849002", display: "Emergency room admission" },
];

const disp = (list, code) => list.find((x) => x.code === code)?.display;

export const fetchPatients = () => api.get("/patients").then((r) => r.data.items || []);
export const fetchLocations = () => api.get("/locations").then((r) => r.data.items || []);

export function fetchEncounter(encounterId) {
  return api.get(`/encounters/${encodeURIComponent(encounterId)}`).then((r) => r.data);
}

export function createEncounter(payload) {
  return api.post("/encounters", payload).then((r) => r.data);
}

export function closeEncounter(encounterId) {
  return api.post(`/encounters/${encodeURIComponent(encounterId)}/close`).then((r) => r.data);
}

export function reopenEncounter(encounterId) {
  return api.post(`/encounters/${encodeURIComponent(encounterId)}/reopen`).then((r) => r.data);
}

export function canCloseEncounter(encounter) {
  return encounter?.status === "in-progress";
}

export function canReopenEncounter(encounter) {
  return encounter?.status === "finished";
}

/** Completed (FHIR finished) encounters stay viewable and read-only. */
export const ENCOUNTER_READ_ONLY_REASON = "Encounter is completed and read-only.";

export function isEncounterReadOnly(encounter) {
  return encounter?.status === "finished";
}

export function canMutateEncounter(encounter) {
  return Boolean(encounter) && !isEncounterReadOnly(encounter);
}

export function encounterReadOnlyError() {
  const error = new Error(ENCOUNTER_READ_ONLY_REASON);
  error.response = { status: 409, data: { detail: ENCOUNTER_READ_ONLY_REASON } };
  return error;
}

/**
 * NoteVersion kinds that may still be saved or signed after Close Encounter when
 * an amendment draft is already open. Creating a new addendum is not exempt.
 * Mirrors AMENDMENT_VERSION_KINDS in backend/v3/encounter.py.
 */
export const AMENDMENT_VERSION_KINDS = ["addendum", "correction"];

export function isAmendmentVersionKind(kind) {
  return AMENDMENT_VERSION_KINDS.includes(kind);
}

/**
 * Client-side check used by every note write helper. Backend 409 remains authoritative.
 * Callers on the addendum / correction path pass the NoteVersion kind they are
 * about to write so a completed encounter does not block it.
 */
export async function assertEncounterWritable(encounterId, { versionKind = null } = {}) {
  if (!encounterId) return null;
  const enc = await fetchEncounter(encounterId);
  if (isEncounterReadOnly(enc) && !isAmendmentVersionKind(versionKind)) {
    throw encounterReadOnlyError();
  }
  return enc;
}

/** Clinical lifecycle is Start Encounter / Close Encounter, not the Edit form. */
export function clinicalStatusForSave(editing) {
  return editing?.status || "planned";
}

export function patientIdOfEncounter(encounter) {
  if (encounter?.patientId) return encounter.patientId;
  const ref = encounter?.subject?.reference || "";
  return ref.split("/")[1] || "";
}

export const encounterNumberOf = (r) =>
  (r?.identifier || []).find((i) =>
    (i.type?.coding || []).some((c) => c.code === "VN")
  )?.value || "—";

export const patientNameOf = (p) => {
  const n = p?.name?.[0];
  if (!n) return "";
  return [...(n.given || []), n.family].filter(Boolean).join(" ");
};

export function formToPayload(f, patients, locations) {
  const pat = (patients || []).find((p) => p.id === f.patientId);
  const loc = (locations || []).find((l) => l.id === f.locationId);

  const payload = {
    status: f.status,
    class: { system: ACTCODE, code: f.class, display: disp(ENCOUNTER_CLASSES, f.class) },
    subject: { reference: `Patient/${f.patientId}`, display: patientNameOf(pat) || undefined },
  };
  if (f.type) {
    const d = disp(ENCOUNTER_TYPES, f.type);
    payload.type = [{ coding: [{ system: SNOMED, code: f.type, display: d }], text: d }];
  }
  if (f.locationId) {
    payload.location = [{
      location: { reference: `Location/${f.locationId}`, display: loc?.name || undefined },
      status: "active",
    }];
  }
  const period = {};
  if (f.start) period.start = new Date(f.start).toISOString();
  if (f.end) period.end = new Date(f.end).toISOString();
  if (period.start || period.end) payload.period = period;
  return payload;
}

const toLocalInput = (iso) => (iso ? iso.slice(0, 16) : "");

export function resourceToForm(r) {
  return {
    patientId: (r.subject?.reference || "").split("/")[1] || "",
    locationId: (r.location?.[0]?.location?.reference || "").split("/")[1] || "",
    status: r.status || "planned",
    class: r.class?.code || "AMB",
    type: r.type?.[0]?.coding?.[0]?.code || "",
    start: toLocalInput(r.period?.start),
    end: toLocalInput(r.period?.end),
  };
}
